import { getToken } from "../storage/authStorage";
import { API_BASE_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export interface GSTINVerificationResult {
  gstin: string;
  legal_name: string;
  trade_name: string;
  status: string;
  address: string;
  state: string;
  city: string;
  pincode: string;
  constitution: string;
  taxpayer_type: string;
  verified_at: string;
}

export interface GSTINVerificationResponse {
  success: boolean;
  data?: {
    verification?: GSTINVerificationResult;
    cached?: boolean;
  } & Partial<GSTINVerificationResult>;
  message?: string;
}

export interface VerificationConfig {
  required: boolean;
  has_credentials: boolean;
}

export interface GstinVerificationSettings {
  cashfree_client_id: string;
  cashfree_client_secret: string;
  gstin_verification_required: string;
}

// POST /api/verification/gstin — verify GSTIN via Cashfree (or return cached)
export const verifyGSTIN = async (
  gstin: string,
): Promise<GSTINVerificationResponse> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/verification/gstin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ gstin }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// POST /api/verification/gstin/refresh — force re-verify
export const refreshGSTINVerification = async (
  gstin: string,
): Promise<GSTINVerificationResponse> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/verification/gstin/refresh`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ gstin }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// GET /api/verification/gstin/{gstin} — check if already verified
export const getGSTINVerification = async (
  gstin: string,
): Promise<GSTINVerificationResponse> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/verification/gstin/${encodeURIComponent(gstin)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// GET /api/verification/config — check if verification is required
export const getVerificationConfig = async (): Promise<VerificationConfig> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/verification/config`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// GET /api/settings/gstin-verification — get settings
export const getGstinVerificationSettings =
  async (): Promise<GstinVerificationSettings> => {
    const token = getToken();

    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/settings/gstin-verification`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || `HTTP error! status: ${response.status}`,
          response.status,
          errorData,
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );
    }
  };

// POST /api/settings/gstin-verification — save settings
export const saveGstinVerificationSettings = async (
  settings: Partial<GstinVerificationSettings>,
): Promise<void> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/settings/gstin-verification`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
